import { cn } from '@pesto/utils';
import { CreditCard, Smartphone } from 'lucide-react';

import type { CardKind, CardViewModel } from '../types/Cards.types';

import { deriveCardKind } from '../utils/deriveCardKind';
import { CardStatusBadge } from './CardStatusBadge';

interface CardVisualProps {
  card: Pick<CardViewModel, 'status' | 'physicalActivatedAt' | 'lastFour' | 'expDate' | 'referenceId' | 'processor'>;
  className?: string;
}

const PROCESSOR_LABELS = new Map([
  ['I2C', 'I2C'],
  ['LITHIC', 'Lithic'],
]);

const SURFACE_BY_KIND: Record<CardKind, string> = {
  'physical-active': 'bg-gradient-to-br from-primary to-primary/80 text-primary-foreground',
  'digital-active': 'bg-gradient-to-br from-primary to-primary/80 text-primary-foreground',
  'not-activated': 'bg-gradient-to-br from-muted to-muted/70 text-muted-foreground border',
  blocked: 'bg-gradient-to-br from-warning to-warning/80 text-warning-foreground',
  closed: 'bg-gradient-to-br from-muted to-muted/60 text-muted-foreground border',
  lost: 'bg-gradient-to-br from-destructive to-destructive/80 text-destructive-foreground',
  stolen: 'bg-gradient-to-br from-destructive to-destructive/80 text-destructive-foreground',
  replaced: 'bg-gradient-to-br from-muted to-muted/60 text-muted-foreground border',
  unknown: 'bg-gradient-to-br from-muted to-muted/60 text-muted-foreground border',
};

const formatLastFour = (lastFour: string | null) => (lastFour ? `•••• ${lastFour}` : '•••• ••••');

export const CardVisual = ({ card, className }: CardVisualProps) => {
  const kind = deriveCardKind(card);
  const surface = SURFACE_BY_KIND[kind];
  const isDigital = kind === 'digital-active';
  const Mark = isDigital ? Smartphone : CreditCard;
  const isCurrent = kind === 'physical-active' || kind === 'digital-active' || kind === 'not-activated';
  const processorLabel = card.processor ? (PROCESSOR_LABELS.get(card.processor) ?? card.processor) : null;

  return (
    <div
      className={cn(
        'relative aspect-[1.586/1] w-full max-w-sm overflow-hidden rounded-2xl p-5 shadow-md',
        surface,
        !isCurrent && 'opacity-95',
        className,
      )}
    >
      <div className="relative flex h-full flex-col justify-between">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2">
            <Mark className="h-5 w-5" />
            <span className="text-xs font-medium uppercase tracking-widest opacity-80">
              {isDigital ? 'Digital' : 'Card'}
              {processorLabel && ` · ${processorLabel}`}
            </span>
          </div>
          <CardStatusBadge
            status={card.status}
            physicalActivatedAt={card.physicalActivatedAt}
            className="border border-current/30 bg-current/10 text-current backdrop-blur-sm hover:bg-current/10"
          />
        </div>

        {!isDigital && <div aria-hidden className="border-current/40 bg-current/15 mt-2 h-7 w-9 rounded-md border" />}

        <div className="space-y-3">
          <div className="font-mono text-lg tracking-[0.25em]">{formatLastFour(card.lastFour)}</div>
          <div className="flex items-end justify-between gap-4 text-xs">
            <div className="min-w-0 space-y-0.5">
              <div className="opacity-70">Reference</div>
              <div className="truncate font-mono text-sm">{card.referenceId ?? '—'}</div>
            </div>
            <div className="space-y-0.5 text-right">
              <div className="opacity-70">Expires</div>
              <div className="font-mono text-sm">{card.expDate ?? '—'}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
